import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import EditNoteIcon from "@mui/icons-material/Edit";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";

import useCheckPermission from "../../../../common/hooks/useCheckPermission";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import CustomerRequestLocationService from "../../../../../infrastructure/api/customerRequestLocation";
import RequestAssignmentApi from "../../../../../infrastructure/api/requestAssignment";
import EnhancedTable from "../../../../components/shared/table";
import RequestFollowUpDialog from "../../../../components/request/RequestFollowUpDialog";
import { getFollowUpRowHighlight, matchesFollowUpStatusFilter } from "../../../../components/request/followUpReminderUtils";
import {
  assignmentOwnerColumn,
  canShowClaimAction,
  enrichAssignmentRows,
  getCurrentUserId,
} from "../../../../components/request/requestAssignmentDisplay";
import { useAssignmentFilteredList } from "../../../../components/request/useAssignmentFilteredList";
import AllRequestsCaseDrawer from "../all-requests/AllRequestsCaseDrawer";
import {
  IN_PROCESS_STATUS_FILTERS,
  ALL_REQUESTS_LOAN_STATUS_FILTERS,
  buildLocationLookupMaps,
  enrichAllRequestRows,
  filterAllRequestsRows,
} from "../all-requests/allRequestsPageUtils";
import PartnerReferralFilterSelect from "../shared/PartnerReferralFilterSelect";
import { usePartnerReferralFilter } from "../shared/partnerReferralFilterUtils";
import { buildProjectWorkspacePath, openProjectWorkspacePathInNewTab, WORKSPACE_LIST_VIEWS } from "../../shared/projectWorkspace/projectWorkspacePaths";
import {
  buildCheckNewRequestModifyPath,
  openCheckNewRequestModifyPath,
} from "../../shared/checkNewRequest/checkNewRequestPageUtils";

const PAGE_TITLE = "مرکز پیگیری — درخواست‌های جاری";
const DETAIL_API = "SLN/SolarentOperator/InProcessRequest";

const provinceOptionId = (p) => p?.id ?? p?.Id ?? p?.value ?? p?.Value ?? "";
const provinceOptionLabel = (p) =>
  p?.provinceName ??
  p?.ProvinceName ??
  p?.title ??
  p?.Title ??
  p?.label ??
  p?.Label ??
  "";
const cityOptionId = (c) => c?.id ?? c?.Id ?? c?.value ?? c?.Value ?? "";
const cityOptionLabel = (c) =>
  c?.cityName ?? c?.CityName ?? c?.title ?? c?.Title ?? c?.label ?? c?.Label ?? "";
const cityOptionProvinceId = (c) =>
  Number(c?.provinceId ?? c?.ProvinceId ?? 0) || 0;
const HISTORY_API = "SLN/SolarentOperator/CheckNewRequest";

const SolarentOperatorInProcessRequestPage = ({ hubMode = false } = {}) => {
  const canEdit = useCheckPermission("edit");
  const currentUserId = getCurrentUserId();
  const [partnerReferralFilter, setPartnerReferralFilter] =
    usePartnerReferralFilter(hubMode);

  const {
    rows: dataList,
    assignmentFilter,
    setAssignmentFilter,
    filterOptions: assignmentFilterOptions,
    showAssignmentUi,
    loading,
    reloadList,
  } = useAssignmentFilteredList(DETAIL_API, false, {
    defaultAssignmentFilter: "all",
  });

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [loanStatusFilter, setLoanStatusFilter] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedFollowUpStatus, setSelectedFollowUpStatus] = useState("");
  const [followUpRequestId, setFollowUpRequestId] = useState(null);
  const [caseDrawer, setCaseDrawer] = useState({ open: false, id: null, row: null });
  const [assignmentBusyId, setAssignmentBusyId] = useState(null);
  const [locationMaps, setLocationMaps] = useState(null);

  useEffect(() => {
    let cancelled = false;
    CustomerRequestLocationService.loadCustomerRequestLocationLists()
      .then(({ provinces: provinceList, cities: cityList }) => {
        if (cancelled) return;
        const nextProvinces = Array.isArray(provinceList) ? provinceList : [];
        const nextCities = Array.isArray(cityList) ? cityList : [];
        setProvinces(nextProvinces);
        setCities(nextCities);
        setLocationMaps(buildLocationLookupMaps(nextProvinces, nextCities));
      })
      .catch(() => {
        if (cancelled) return;
        setProvinces([]);
        setCities([]);
        setLocationMaps(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredCities = useMemo(() => {
    if (!provinceFilter) return [];
    return cities.filter(
      (c) => cityOptionProvinceId(c) === Number(provinceFilter)
    );
  }, [cities, provinceFilter]);

  const rows = useMemo(
    () =>
      enrichAllRequestRows(
        enrichAssignmentRows(dataList),
        currentUserId,
        locationMaps
      ),
    [dataList, currentUserId, locationMaps]
  );

  const filteredRows = useMemo(
    () =>
      filterAllRequestsRows(rows, {
        searchText,
        statusFilter,
        partnerReferralFilter,
        provinceFilter,
        cityFilter,
        loanStatusFilter,
      }).filter((row) => matchesFollowUpStatusFilter(row, selectedFollowUpStatus)),
    [
      rows,
      searchText,
      statusFilter,
      partnerReferralFilter,
      provinceFilter,
      cityFilter,
      loanStatusFilter,
      selectedFollowUpStatus,
    ]
  );

  const headCells = useMemo(() => {
    const cols = [
      { id: "referenceID", label: "کد درخواست" },
      { id: "applicantFullName", label: "متقاضی" },
      { id: "provinceName", label: "استان" },
      { id: "cityName", label: "شهرستان" },
      { id: "mobile", label: "موبایل" },
      { id: "partnerReferralDisplayName", label: "معرف (سطح یک)" },
      { id: "requestConfirmTitle", label: "وضعیت درخواست" },
      { id: "stepTitle", label: "مرحله فعلی" },
      {
        id: "scheduledVisitTime",
        label: "زمان بازدید",
        type: "date",
      },
      {
        id: "workflowV2NextAction",
        label: "اقدام بعدی",
      },
    ];
    if (showAssignmentUi) cols.push(assignmentOwnerColumn);
    cols.push(
      {
        id: "leadPriorityTitle",
        label: "اولویت",
        type: "chip",
        model: { colorField: "leadPriorityColor" },
      },
      {
        id: "followUpReminderTitle",
        label: "وضعیت پیگیری",
        type: "chip",
        model: { colorField: "followUpReminderColor" },
      },
      { id: "updateOn", label: "آخرین بروزرسانی", type: "date" }
    );
    return cols;
  }, [showAssignmentUi]);

  const onOpenCase = useCallback((row) => {
    setCaseDrawer({ open: true, id: row.id, row });
  }, []);

  const onEditClicked = useCallback((row) => {
    const path = buildCheckNewRequestModifyPath(row, "solarentOperator");
    if (path) openCheckNewRequestModifyPath(path);
  }, []);

  const onOpenWorkspace = useCallback(
    (row) => {
      const path = buildProjectWorkspacePath(row, "solarentOperator", {
        listView: WORKSPACE_LIST_VIEWS.inProcess,
      });
      if (path) openProjectWorkspacePathInNewTab(path);
    },
    []
  );

  const handleClaim = useCallback(
    async (row) => {
      setAssignmentBusyId(row.id);
      try {
        const res = await RequestAssignmentApi.claim(
          "SolarentOperator",
          Number(row.id)
        );
        if (res?.isSuccess) {
          toast.success(res.message || "درخواست با موفقیت دریافت شد.");
          reloadList();
        } else {
          toast.error(
            res?.message ||
              "این درخواست قبلاً توسط کارشناس دیگری دریافت شده است."
          );
          reloadList();
        }
      } catch {
        toast.error("خطا در دریافت درخواست");
      } finally {
        setAssignmentBusyId(null);
      }
    },
    [reloadList]
  );

  const handleRelease = useCallback(
    async (row) => {
      setAssignmentBusyId(row.id);
      try {
        const res = await RequestAssignmentApi.release(
          "SolarentOperator",
          Number(row.id)
        );
        if (res?.isSuccess) {
          toast.success(res.message || "درخواست آزاد شد.");
          reloadList();
        } else {
          toast.error(res?.message || "خطا در آزادسازی درخواست");
        }
      } catch {
        toast.error("خطا در آزادسازی درخواست");
      } finally {
        setAssignmentBusyId(null);
      }
    },
    [reloadList]
  );

  const tableActions = useMemo(() => {
    const actions = [
      {
        icon: <FolderOpenIcon fontSize="small" />,
        onClick: onOpenWorkspace,
        color: "primary",
        tooltip: "پرونده پروژه (Workspace)",
      },
      {
        icon: <ContentPasteSearchIcon fontSize="small" />,
        onClick: onOpenCase,
        color: "info",
        tooltip: "بررسی پرونده",
      },
    ];

    if (canEdit) {
      actions.splice(1, 0, {
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: onEditClicked,
        color: "secondary",
        tooltip: "ویرایش درخواست",
      });
      if (showAssignmentUi) {
        actions.push({
          icon: <PersonAddAlt1Icon fontSize="small" />,
          onClick: handleClaim,
          color: "success",
          tooltip: "دریافت درخواست",
          hidden: (row) =>
            !canShowClaimAction(row, showAssignmentUi) ||
            assignmentBusyId === row.id,
        });
        actions.push({
          icon: <LockOpenIcon fontSize="small" />,
          onClick: handleRelease,
          color: "warning",
          tooltip: "آزادسازی",
          hidden: (row) => !row.canRelease || assignmentBusyId === row.id,
        });
      }

      actions.push({
        icon: <EditNoteIcon fontSize="small" />,
        onClick: (row) => setFollowUpRequestId(row.id),
        color: "secondary",
        tooltip: "ثبت پیگیری",
      });
    }

    return actions;
  }, [
    canEdit,
    showAssignmentUi,
    assignmentBusyId,
    onOpenCase,
    handleClaim,
    handleRelease,
    onEditClicked,
    onOpenWorkspace,
  ]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {!hubMode ? <BreadcrumbsComponent /> : null}
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={PAGE_TITLE}
            subheader="جستجو و مشاهده پرونده درخواست‌های در حال اجرا"
            action={
              <IconButton
                color="primary"
                onClick={() => reloadList()}
                aria-label="بروزرسانی لیست"
              >
                <SyncIcon />
              </IconButton>
            }
          />

          <CardContent>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexWrap: { xs: "wrap", lg: "nowrap" },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="جستجو بر اساس نام، موبایل، کد ملی، شناسه قبض، کد پیگیری، شهر، وضعیت..."
                sx={{ flex: "1 1 240px", minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Chip
                size="small"
                variant="outlined"
                label={`${filteredRows.length} مورد`}
                sx={{ flexShrink: 0 }}
              />

              {showAssignmentUi ? (
                <FormControl size="small" sx={{ flex: "0 0 170px", minWidth: 150 }}>
                  <InputLabel>وضعیت مسئولیت</InputLabel>
                  <Select
                    value={assignmentFilter}
                    label="وضعیت مسئولیت"
                    onChange={(e) => setAssignmentFilter(e.target.value)}
                  >
                    {assignmentFilterOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}

              <Box sx={{ flex: "0 0 190px", minWidth: 170 }}>
                <PartnerReferralFilterSelect
                  value={partnerReferralFilter}
                  onChange={setPartnerReferralFilter}
                  rows={rows}
                />
              </Box>

              <FormControl size="small" sx={{ flex: "0 0 170px", minWidth: 150 }}>
                <InputLabel>وضعیت پیگیری</InputLabel>
                <Select
                  value={selectedFollowUpStatus}
                  label="وضعیت پیگیری"
                  onChange={(event) => setSelectedFollowUpStatus(event.target.value)}
                >
                  <MenuItem value="">همه</MenuItem>
                  <MenuItem value="overdue">پیگیری عقب افتاده</MenuItem>
                  <MenuItem value="today">پیگیری امروز</MenuItem>
                  <MenuItem value="none">بدون پیگیری</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: "0 0 150px", minWidth: 140 }}>
                <InputLabel>استان</InputLabel>
                <Select
                  value={provinceFilter}
                  label="استان"
                  onChange={(e) => {
                    setProvinceFilter(e.target.value);
                    setCityFilter("");
                  }}
                >
                  <MenuItem value="">همه</MenuItem>
                  {provinces.map((p) => {
                    const id = provinceOptionId(p);
                    return (
                      <MenuItem key={String(id)} value={id}>
                        {provinceOptionLabel(p)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{ flex: "0 0 150px", minWidth: 140 }}
                disabled={!provinceFilter}
              >
                <InputLabel>شهر</InputLabel>
                <Select
                  value={cityFilter}
                  label="شهر"
                  onChange={(e) => setCityFilter(e.target.value)}
                >
                  <MenuItem value="">همه</MenuItem>
                  {filteredCities.map((c) => {
                    const id = cityOptionId(c);
                    return (
                      <MenuItem key={String(id)} value={id}>
                        {cityOptionLabel(c)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: "0 0 180px", minWidth: 160 }}>
                <InputLabel>وضعیت وام</InputLabel>
                <Select
                  value={loanStatusFilter}
                  label="وضعیت وام"
                  onChange={(e) => setLoanStatusFilter(e.target.value)}
                >
                  {ALL_REQUESTS_LOAN_STATUS_FILTERS.map((opt) => (
                    <MenuItem key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {!hubMode ? (
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ mr: 0.5 }}
                >
                  وضعیت:
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={statusFilter}
                  exclusive
                  onChange={(_, val) => setStatusFilter(val ?? "")}
                  sx={{ flexWrap: "wrap" }}
                >
                  {IN_PROCESS_STATUS_FILTERS.map((opt) => (
                    <ToggleButton key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            ) : null}

            <EnhancedTable
              rowKey="id"
              rows={filteredRows}
              headCells={headCells}
              getRowStatus={getFollowUpRowHighlight}
              defaultSort={{ orderBy: "updateOn", order: "desc" }}
              pageSize={100}
              showFilter={false}
              loading={loading}
              tableMinWidth="auto"
              actionsMode="menu"
              actions={tableActions}
            />
          </CardContent>
        </Card>
      </Grid>

      <RequestFollowUpDialog
        open={!!followUpRequestId}
        onClose={() => setFollowUpRequestId(null)}
        source="SolarentOperator"
        requestInfoId={followUpRequestId}
        onSubmitted={reloadList}
      />

      <AllRequestsCaseDrawer
        open={caseDrawer.open}
        requestId={caseDrawer.id}
        listRow={caseDrawer.row}
        onClose={() => setCaseDrawer({ open: false, id: null, row: null })}
        detailApi={DETAIL_API}
        historyApi={HISTORY_API}
        listView={WORKSPACE_LIST_VIEWS.inProcess}
      />
    </Grid>
  );
};

export default SolarentOperatorInProcessRequestPage;
